/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";

import { ToasterContainer } from "baseui/toast";
import { HeadingXXLarge, LabelLarge } from "baseui/typography";

import { gql, useQuery } from "@apollo/client";

import appInfo from "./Api/Gql/AppInfo";
import { AppInfo } from "Api";

import { Endpoints } from "./Utils/Common";

import { DexterAuthContext } from "./Utils/AuthContext";
import {
  ThemeProvider,
  useDexterTheme,
} from "./DesignSystem/Theme/ThemeContext";
import { CenteredContent } from "./DesignSystem/Containers";
import { MigrationUpdate, showMigrationPage } from "./MigrationUpdate";
import { UserApp } from "./UserApp";
import { notify } from "./Shared/Notify";
import { handleApolloError } from "./Shared/Errors";
import { setIsSensitiveSecurityContextVar } from "./GlobalState";
import { AppInitialization, isAppInitialized } from "./AppInitialization";
import { useAsyncA } from "./Utils/UseAsyncHook";
import { APP_TITLE_TITLECASE } from "./Utils/SiteProps";
import { LOGO_URL } from "./Utils/SiteLogo.mjs";
import { AxiosContext } from "./Utils/AuthContext";
import { Input } from "baseui/input";
import { Button } from "baseui/button";
import { StyledLink } from "baseui/link";
import { AuthView } from "./Utils/AuthHelpers";
import { RegistrationForm } from "./RegistrationForm";
import { ChangePasswordModal } from "./Shared/ChangePasswordModal";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { Hide, Show } from "baseui/icon";
import { getTooltipOverrides } from "./DesignSystem/TooltipStyles";

export const DexterApp = (): JSX.Element => {
  const dexterAuth = React.useContext(DexterAuthContext);
  return (
    <ThemeProvider>
      {dexterAuth.isAuthorized() ? (
        <CheckAppReady />
      ) : (
        <ToasterContainer placement={PLACEMENT.topRight}>
          <UserUnauthorized />
        </ToasterContainer>
      )}
    </ThemeProvider>
  );
};

const UserUnauthorized = (): JSX.Element => {
  const [appInitialized, setAppInitialized] = React.useState(true);
  const [view, setView] = React.useState<AuthView>(AuthView.Login);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
  const dexterAuth = React.useContext(DexterAuthContext);

  const { data } = useAsyncA({
    asyncFunc: isAppInitialized,
    immediate: true,
    initialData: true,
  });

  React.useEffect(() => {
    setAppInitialized(data);
  }, [data]);

  React.useEffect(() => {
    setIsPasswordModalOpen(
      dexterAuth && dexterAuth.user && dexterAuth.user.mustChangePassword
    );
  }, [dexterAuth]);

  if (!appInitialized) {
    return <AppInitialization onInitialization={setAppInitialized} />;
  }

  return view === AuthView.Register ? (
    <RegistrationForm onBackToLogin={() => setView(AuthView.Login)} />
  ) : (
    <>
      <LoginForm onShowRegister={() => setView(AuthView.Register)} />
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        showMustChangeMessage
      />
    </>
  );
};

const CheckAppReady = (): JSX.Element => {
  const [migrationIsNeeded, setMigrationIsNeeded] = React.useState(false);
  const [appInfoValues, setAppInfoValues] = React.useState({} as AppInfo);
  const [ignoreRequiredMigration, setIgnoreRequiredMigration] =
    React.useState(false);
  const [isSeeding, setIsSeeding] = React.useState(true);
  const axiosContext = React.useContext(AxiosContext);

  React.useEffect(() => {
    axiosContext
      .post(Endpoints.SeedUserDemo)
      .catch((err) => {
        console.error("Could not seed demo data:", err);
      })
      .finally(() => {
        setIsSeeding(false);
      });
  }, [axiosContext]);

  useQuery(gql(appInfo), {
    onCompleted: (data: { appInfo: AppInfo }) => {
      setIsSensitiveSecurityContextVar(
        data?.appInfo.configurations.isSensitiveSecurityContext
      );
      setAppInfoValues(data?.appInfo);
      setMigrationIsNeeded(showMigrationPage(data?.appInfo.schemaStatus));
    },
    onError: (error) => {
      handleApolloError(
        error,
        "An error occurred while attempting to query for AppInfo."
      );
    },
  });

  const handleMigrationCompleted = (): void => {
    setMigrationIsNeeded(false);
    notify.positive(
      `Migration to version "${appInfoValues.schemaStatus.expectedMigrationId}" complete.`
    );
  };

  // Hold rendering until seeding resolves
  if (isSeeding) {
    return (
      <CenteredContent>
        <img src={LOGO_URL.toString()} height="200rem" />
      </CenteredContent>
    );
  }

  return !migrationIsNeeded || ignoreRequiredMigration ? (
    <div style={{ overflowX: "hidden" }}>
      <UserApp />
    </div>
  ) : (
    <ToasterContainer placement={PLACEMENT.topRight}>
      <MigrationUpdate
        appInfo={appInfoValues}
        onMigrationIgnore={() => setIgnoreRequiredMigration(true)}
        onMigrationComplete={() => handleMigrationCompleted()}
      />
    </ToasterContainer>
  );
};

// Updated show/hide icons as custom components for tooltip consistency
const MaskToggleShowIconNoTitle = React.forwardRef<
  SVGSVGElement,
  React.ComponentProps<typeof Show>
>((props, ref) => {
  const { currentTheme } = useDexterTheme();
  return (
    <StatefulTooltip
      placement={PLACEMENT.right}
      content={"Show Password Text"}
      overrides={getTooltipOverrides(currentTheme)}
    >
      <Show {...props} ref={ref} title="" />
    </StatefulTooltip>
  );
});

MaskToggleShowIconNoTitle.displayName = "MaskToggleShowIconNoTitle";

const MaskToggleHideIconNoTitle = React.forwardRef<
  SVGSVGElement,
  React.ComponentProps<typeof Hide>
>((props, ref) => {
  const { currentTheme } = useDexterTheme();
  return (
    <StatefulTooltip
      placement={PLACEMENT.right}
      content={"Hide Password Text"}
      overrides={getTooltipOverrides(currentTheme)}
    >
      <Hide {...props} ref={ref} title="" />
    </StatefulTooltip>
  );
});

MaskToggleHideIconNoTitle.displayName = "MaskToggleHideIconNoTitle";

const LoginForm = ({
  onShowRegister,
}: {
  onShowRegister: () => void;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const dexterAuth = React.useContext(DexterAuthContext);
  const [identifier, setIdentifier] = React.useState("");
  const [password, setPassword] = React.useState("");

  const canSubmit = identifier.trim() !== "" && password.trim() !== "";

  const handleLogin = (): void => {
    if (canSubmit) {
      dexterAuth.loginUser(identifier, password);
    }
  };

  const primaryButtonOverrides = {
    BaseButton: {
      style: {
        backgroundColor: currentTheme.colors.primaryButtonColor,
        color: currentTheme.colors.primaryElementFontColor,
      },
    },
  };

  const inputOverrides = {
    Root: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        borderTopColor: currentTheme.colors.dropdownBorderColor,
        borderBottomColor: currentTheme.colors.dropdownBorderColor,
        borderLeftColor: currentTheme.colors.dropdownBorderColor,
        borderRightColor: currentTheme.colors.dropdownBorderColor,
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
        caretColor: currentTheme.colors.dropdownText,
        "::placeholder": {
          color: currentTheme.colors.secondaryTextColor,
        },
      },
    },
    InputContainer: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
      },
    },
    MaskToggleButton: {
      props: { title: null },
      style: ({ $isHovered }: { $isHovered: boolean }) => ({
        backgroundColor: $isHovered
          ? currentTheme.colors.dropdownItemHoverBackground
          : currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
      }),
    },
    MaskToggleShowIcon: MaskToggleShowIconNoTitle,
    MaskToggleHideIcon: MaskToggleHideIconNoTitle,
  };

  return (
    <CenteredContent>
      <img src={LOGO_URL.toString()} height="200rem" />
      <HeadingXXLarge>{`Welcome to ${APP_TITLE_TITLECASE}!`}</HeadingXXLarge>
      <LabelLarge>{"Log in to get started"}</LabelLarge>
      <div style={{ width: "20rem", paddingBottom: "1rem" }}>
        <div style={{ paddingBottom: "1rem" }}>
          <Input
            value={identifier}
            onChange={(e) => setIdentifier(e.currentTarget.value)}
            placeholder="Email or username"
            data-testid="login-identifier"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            overrides={inputOverrides}
          />
        </div>
        <Input
          value={password}
          type="password"
          onChange={(e) => setPassword(e.currentTarget.value)}
          placeholder="Password"
          data-testid="login-password"
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          overrides={inputOverrides}
        />
      </div>
      <Button
        disabled={!canSubmit}
        data-testid="login-button"
        onClick={handleLogin}
        overrides={primaryButtonOverrides}
      >
        Login
      </Button>
      <div style={{ paddingTop: "1rem" }}>
        <LabelLarge>
          {"Need an account? "}
          <StyledLink
            href="#"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onShowRegister();
            }}
            data-testid="register-link"
          >
            Register
          </StyledLink>
        </LabelLarge>
      </div>
    </CenteredContent>
  );
};
